import React from "react";
import { Box, Flex, HStack, IconButton, Popover, PopoverBody, PopoverContent, PopoverTrigger, Spacer } from "@chakra-ui/react";
import { FilterSettings } from "../filter-settings/FilterSettings";
import SidebarToggle from "../../components/toolbar/SidebarToggle";
import CustomSwitch from "../../components/design_library/toolbar/Switch";
import PageHeading from "../../components/design_library/PageHeading";
import { RiExchangeBoxFill } from "react-icons/ri";
import { MdCategory, MdBook } from "react-icons/md";
import { BiGroup } from "react-icons/bi";
import { FiEyeOff } from "react-icons/fi";
import { FiMoreHorizontal } from "react-icons/fi";
import { FixedDateRangeSelector } from "../../components";
import { KeyValuePair, KnowledgeBaseHelperTopics } from "../../types";
import { AutoSync } from "../AutoSync/AutoSync";
import KnowledgeBaseHelper from "../knowledgebase/KnowledgeBaseHelper";
import Uploads from "../uploads/Uploads";
import { useAtomValue } from "jotai";
import { ViewModeAtom } from "../../store";

interface CashflowsToolbarProps {
  showUnCategorised: boolean;
  setShowUnCategorised: (value: boolean) => void;
  showUnBooked: boolean;
  setShowUnBooked: (value: boolean) => void;
  showNoPositionGrouping: boolean;
  setShowNoPositionGrouping: (value: boolean) => void;
  showHidden: boolean;
  setShowHidden: (value: boolean) => void;
  setSelectedDateRange: (item: KeyValuePair) => void;
}

export const CashflowsToolbar = (props: CashflowsToolbarProps) => {
  const { setSelectedDateRange } = props;
  const viewMode = useAtomValue(ViewModeAtom);
  
  return (
    <Flex className="grid-toolbar-hook" mx="2" mb={2}>
      <HStack className="grid-toolbar-select" wrap={"wrap"} maxW="100vw" w="100%" spacing={2} overflowX="auto" overflowY="hidden">
        <SidebarToggle />

        <PageHeading
          title="Cashflows"
          description="View and manage your cashflows. Filter, group, and sort your trades to get the insights you need."
          icon={<RiExchangeBoxFill />}
        />

        {viewMode === "advanced" && <FilterSettings />}

        <FixedDateRangeSelector
          onDateRangeSelected={setSelectedDateRange}
          storageKey="cashflows"
          defaultValue="1y"
        />

        <CustomSwitch
          icon={<MdCategory size={14} />}
          id="isChecked"
          isChecked={props.showUnCategorised}
          onChange={(e: any) => {
            props.setShowUnCategorised(!props.showUnCategorised);
          }}
          mr={2}
          className="custom-switch-with-elipsis"
        >
          Uncategorised
        </CustomSwitch>

        {viewMode === "advanced" && (
          <CustomSwitch
            icon={<MdBook size={14} />}
            id="isChecked"
            isChecked={props.showUnBooked}
            onChange={(e: any) => {
              props.setShowUnBooked(!props.showUnBooked);
            }}
            mr={2}
            className="custom-switch-with-elipsis"
          >
            Unbooked
          </CustomSwitch>
        )}

        {viewMode === "advanced" && (
          <CustomSwitch
            icon={<BiGroup size={14} />}
            id="isChecked"
            className="custom-switch-with-elipsis"
            isChecked={props.showNoPositionGrouping}
            onChange={(e: any) => {
              props.setShowNoPositionGrouping(!props.showNoPositionGrouping);
            }}
            mr={2}
          >
            No position group
          </CustomSwitch>
        )}

        <CustomSwitch
          icon={<FiEyeOff size={14} />}
          id="isChecked"
          className="custom-switch-with-elipsis"
          isChecked={props.showHidden}
          onChange={(e: any) => {
            props.setShowHidden(!props.showHidden);
          }}
          mr={2}
        >
          Hidden cashflows
        </CustomSwitch>

        <Spacer display={{ base: "none", md: "block" }} />

        {/* Desktop actions */}
        <HStack display={{ base: "none", md: "flex" }}>
          <AutoSync minimalGui={true} />
          <KnowledgeBaseHelper topic={KnowledgeBaseHelperTopics.TRANSACTIONS} />
        </HStack>

        {/* Keep Add assets visible at all sizes */}
        <Box>
          <Uploads />
        </Box>

        {/* Mobile overflow menu */}
        <Box display={{ base: "block", md: "none" }}>
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton aria-label="More" icon={<FiMoreHorizontal />} size="sm" variant="ghost" />
            </PopoverTrigger>
            <PopoverContent p={2} bg="var(--bg)">
              <PopoverBody>
                <HStack spacing={3} alignItems="center">
                  <AutoSync minimalGui={true} />
                  <KnowledgeBaseHelper topic={KnowledgeBaseHelperTopics.TRANSACTIONS} />
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </HStack>
    </Flex>
  );
};
